import { memo, useState, useCallback, useRef, useEffect } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import { getIconDef } from '@/lib/icons';
import { IconRenderer } from '@/components/ui/IconRenderer';
import { useEditorStore } from '../store';
import { useBlastRadius } from '../lib/blastRadiusContext';

interface CloudNodeData {
  label: string;
  nodeType: string;
  metadata?: Record<string, string>;
  [key: string]: unknown;
}

function CloudNodeComponent({ id, data, selected }: NodeProps) {
  const { label, nodeType } = data as CloudNodeData;
  const icon = getIconDef(nodeType);
  const updateNodeData = useEditorStore((s) => s.updateNodeData);
  const { focus, affected, spofs } = useBlastRadius();

  // Three modes for the node visual:
  //   - epicenter: this is the failed node — strong red, pulsing.
  //   - affected: in the blast set — red tint, no pulse.
  //   - dimmed: blast mode active but this node is unaffected — fade out.
  //   - normal: blast mode off.
  // The SPOF badge ⚠ is independent of blast focus; it always shows on
  // nodes whose failure would cascade to >25% of the diagram.
  const isEpicenter = focus === id;
  const isAffected = focus !== null && affected.has(id) && !isEpicenter;
  const isDimmed = focus !== null && !affected.has(id);
  const isSPOF = spofs.has(id);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(label);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const commitLabel = useCallback(() => {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== label) {
      updateNodeData(id, { label: trimmed });
    } else {
      setDraft(label);
    }
    setEditing(false);
  }, [draft, label, id, updateNodeData]);

  const handleDoubleClick = useCallback(() => {
    setDraft(label);
    setEditing(true);
  }, [label]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') commitLabel();
      else if (e.key === 'Escape') { setDraft(label); setEditing(false); }
    },
    [commitLabel, label],
  );

  // Compose the outer ring + background. Blast styling takes precedence
  // over plain selection — when blast mode is on, the regular brand-blue
  // selected ring would fight the red epicenter signal.
  let outerClasses = 'border-line-default/60 hover:border-line-strong shadow-clyra-sm';
  if (isEpicenter) {
    outerClasses =
      'border-danger ring-2 ring-danger/40 shadow-[0_0_20px_rgba(239,68,68,0.35)] animate-pulse';
  } else if (isAffected) {
    outerClasses = 'border-danger/40 ring-1 ring-danger/20 bg-danger/[0.04]';
  } else if (isDimmed) {
    outerClasses = 'border-line-default/30 opacity-30 grayscale';
  } else if (selected) {
    outerClasses = 'border-brand-500/50 shadow-clyra-glow ring-1 ring-brand-500/20';
  }

  return (
    <div
      className={`relative flex flex-col items-center rounded-clyra-xl border bg-clyra-raised/95 backdrop-blur-sm px-4 py-3 transition-all duration-150 min-w-[110px] max-w-[160px] ${outerClasses}`}
    >
      {isSPOF && !isEpicenter && !isAffected && (
        <span
          title="Single Point of Failure — many things depend on this"
          className="absolute -top-2 -right-2 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-amber-500 text-[10px] font-bold text-white shadow-sm [body.paper_&]:bg-amber-600"
        >
          !
        </span>
      )}
      <Handle id="top-target" type="target" position={Position.Top} className="!bg-brand-500/70 !w-1.5 !h-1.5 !border-0 !-top-[3px]" />
      <Handle id="top-source" type="source" position={Position.Top} className="!bg-brand-500/70 !w-1.5 !h-1.5 !border-0 !-top-[3px]" />
      <Handle id="left-target" type="target" position={Position.Left} className="!bg-brand-500/70 !w-1.5 !h-1.5 !border-0 !-left-[3px]" />
      <Handle id="left-source" type="source" position={Position.Left} className="!bg-brand-500/70 !w-1.5 !h-1.5 !border-0 !-left-[3px]" />

      <div
        className="flex h-9 w-9 items-center justify-center rounded-clyra"
        style={{ backgroundColor: `${icon.color}12`, color: icon.color }}
      >
        <IconRenderer name={icon.icon} size={20} strokeWidth={1.5} />
      </div>

      {editing ? (
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitLabel}
          onKeyDown={handleKeyDown}
          className="mt-1.5 w-[120px] rounded-clyra border border-brand-500/40 bg-clyra-bg px-1.5 py-0.5 text-center text-tiny font-medium text-ink-primary outline-none"
        />
      ) : (
        <span
          onDoubleClick={handleDoubleClick}
          className="mt-1.5 cursor-text text-tiny font-medium text-ink-primary text-center leading-tight max-w-[130px] truncate"
          title={label}
        >
          {label}
        </span>
      )}

      <span
        className="mt-0.5 text-micro font-medium truncate max-w-[130px]"
        style={{ color: `${icon.color}aa` }}
      >
        {icon.label}
      </span>

      <Handle id="bottom-target" type="target" position={Position.Bottom} className="!bg-brand-500/70 !w-1.5 !h-1.5 !border-0 !-bottom-[3px]" />
      <Handle id="bottom-source" type="source" position={Position.Bottom} className="!bg-brand-500/70 !w-1.5 !h-1.5 !border-0 !-bottom-[3px]" />
      <Handle id="right-target" type="target" position={Position.Right} className="!bg-brand-500/70 !w-1.5 !h-1.5 !border-0 !-right-[3px]" />
      <Handle id="right-source" type="source" position={Position.Right} className="!bg-brand-500/70 !w-1.5 !h-1.5 !border-0 !-right-[3px]" />
    </div>
  );
}

export const CloudNode = memo(CloudNodeComponent);
